import { Button, Title1, makeStyles, tokens } from '@fluentui/react-components';
import { AuthProvider, VersionBanner, useAuth } from '@worklogix/web-common';

import { PlatformGate } from './platform/PlatformGate';

const useStyles = makeStyles({
  signIn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalL,
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
  },
});

/**
 * Sign-in prompt shown when no account is present. Lives inside the MSAL
 * provider (so {@link useAuth} works) but outside the platform gate. Ported from
 * the pre-split App.tsx; identity/login is delegated to web-common's useAuth.
 */
function SignInPrompt(): JSX.Element {
  const styles = useStyles();
  const { login } = useAuth();

  return (
    <main className={styles.signIn}>
      <Title1>WorkLogix Platform</Title1>
      <p>Please sign in to continue.</p>
      <Button appearance="primary" onClick={() => void login()}>
        Sign in
      </Button>
    </main>
  );
}

/**
 * Auth-gated switch beneath {@link AuthProvider}. Once an account is present the
 * user is handed to {@link PlatformGate} (which probes `GET /platform/access`);
 * otherwise the {@link SignInPrompt} is shown. Branching on `useAuth().isAuthenticated`
 * keeps this app dependent on the web-common barrel alone — no direct @azure/msal-react
 * import — per Contract §B (shared code only via @worklogix/web-common).
 */
function AuthGate(): JSX.Element {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <PlatformGate /> : <SignInPrompt />;
}

/**
 * Console application root. Establishes the MSAL auth context, keeps the
 * version-check banner visible in every state, and — once authenticated — hands
 * off to {@link PlatformGate}, which fails closed to a NoAccess screen for any
 * account without platform-admin access.
 */
export function PlatformApp(): JSX.Element {
  return (
    <AuthProvider>
      <VersionBanner />
      <AuthGate />
    </AuthProvider>
  );
}
